import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { RootState, AppAsyncThunkConfig } from "store";

export interface PartType {
  id?: string | null;
  description?: string | null;
  partNumber?: string | null;
  barcode?: string | null;
  quantity?: number | null;
  stockStore?: string | null;
  isNonStock?: boolean | null;
  stockName?: string | null;
  stockId?: string | null;
  manufacturerId?: number | null;
}

export type State = {
  availableParts: (PartType | null)[];
  requestableParts: (PartType | null)[];
  loading: boolean;
  error: string | null;
};

const initialState: State = {
  availableParts: [],
  requestableParts: [],
  loading: false,
  error: null,
};

export const fetchAvailableParts = createAsyncThunk<
  PartType[],
  { locationId: string }[],
  AppAsyncThunkConfig
>(
  "addPart/fetchAvailableParts",
  async (locations, { rejectWithValue, extra: { sdk } }) => {
    try {
      const { data, errors } = await graphqlRequest(sdk.getAvailableParts, {
        variables: { locations },
      });

      if (errors || !data?.parts) {
        return rejectWithValue("Failed to fetch parts");
      }

      return data.parts;
    } catch (error) {
      return rejectWithValue("Unexpected error occurred");
    }
  }
);

export const fetchRequestableParts = createAsyncThunk<
  PartType[],
  { locationId: string }[],
  AppAsyncThunkConfig
>(
  "addPart/fetchRequestableParts",
  async (locations, { rejectWithValue, extra: { sdk } }) => {
    try {
      const { data, errors } = await graphqlRequest(sdk.getRequestableParts, {
        variables: { locations },
      });

      if (errors || !data?.requestableParts) {
        return rejectWithValue("Failed to fetch requestable parts");
      }

      return data.requestableParts;
    } catch (error) {
      return rejectWithValue("Unexpected error occurred");
    }
  }
);

const addPartSlice = createSlice({
  name: "addPart",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableParts.fulfilled, (state, action) => {
        state.loading = false;
        state.availableParts = action.payload;
      })
      .addCase(fetchAvailableParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchRequestableParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequestableParts.fulfilled, (state, action) => {
        state.loading = false;
        state.requestableParts = action.payload;
      })
      .addCase(fetchRequestableParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
  },
});

export default addPartSlice.reducer;

export const selectAvailableParts = (state: RootState) => state.addPart.availableParts;
export const selectPartsLoading = (state: RootState) => state.addPart.loading;
export const selectPartsError = (state: RootState) => state.addPart.error;
